/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { isSameMonth } from 'date-fns/isSameMonth';
import { parseISO } from 'date-fns/parseISO';
import { formatDate } from 'uplisting-frontend/helpers/date-format';
import { SecurityDepositStatus } from 'uplisting-frontend/models/security-deposit';
import { SafeguestVerificationStatus } from 'uplisting-frontend/models/booking';
import { BookingRentalAgreementStatus } from 'uplisting-frontend/models/booking-rental-agreement';
import { GuestIdentityVerificationStatus } from 'uplisting-frontend/models/occasion';
import {
  DepositOutcome,
  ProtectionState,
  chipClass,
  depositReading,
  occasionState,
} from 'uplisting-frontend/utils/protection-status';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  occasions: ModelRegistry['occasion'][];
  onSelect: (bookingId: string) => void;
}

interface UiProtectionBookingsTableSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export interface IBookingRow {
  id: string;
  bookingId: string;
  guestName: string;
  property: string;
  statusKey: string;
  statusClass: string;
  needsYou: boolean;
  depositKey: string;
  depositAmount?: number;
  claimedAmount?: string;
  depositTotal?: string;
  currency: string;
  identityKey: string;
  agreementKey: string;
  stay: string;
}

const OUTCOME_KEYS: Record<DepositOutcome, string> = {
  [DepositOutcome.claimed]: 'claimed',
  [DepositOutcome.partiallyClaimed]: 'partially_claimed',
  [DepositOutcome.released]: 'released',
  [DepositOutcome.autoReleased]: 'auto_released',
  [DepositOutcome.expired]: 'expired',
};

const DEPOSIT_KEYS: Record<SecurityDepositStatus, string> = {
  [SecurityDepositStatus.needsPaymentInformation]: 'needs_payment_details',
  [SecurityDepositStatus.hasPaymentInformation]: 'has_payment_details',
  [SecurityDepositStatus.authorized]: 'authorised',
  [SecurityDepositStatus.collected]: 'collected',
  [SecurityDepositStatus.failed]: 'failed_to_authorise',
  [SecurityDepositStatus.failing]: 'retrying',
  [SecurityDepositStatus.overridden]: 'waived',
};

const SAFEGUEST_KEYS: Record<SafeguestVerificationStatus, string> = {
  [SafeguestVerificationStatus.pending]: 'safeguest_pending',
  [SafeguestVerificationStatus.green]: 'safeguest_green',
  [SafeguestVerificationStatus.yellow]: 'safeguest_yellow',
  [SafeguestVerificationStatus.red]: 'safeguest_red',
};

const IDENTITY_KEYS: Record<GuestIdentityVerificationStatus, string> = {
  [GuestIdentityVerificationStatus.requiresInput]: 'could_not_be_verified',
  [GuestIdentityVerificationStatus.requiresAction]: 'waiting_on_the_guest',
  [GuestIdentityVerificationStatus.processing]: 'checking',
  [GuestIdentityVerificationStatus.verified]: 'verified',
  [GuestIdentityVerificationStatus.succeeded]: 'verified',
  [GuestIdentityVerificationStatus.overridden]: 'accepted_manually',
};

const AGREEMENT_KEYS: Record<BookingRentalAgreementStatus, string> = {
  [BookingRentalAgreementStatus.pending]: 'unsigned',
  [BookingRentalAgreementStatus.processing]: 'processing',
  [BookingRentalAgreementStatus.processed]: 'signed',
  [BookingRentalAgreementStatus.markedAsSigned]: 'signed',
};

export default class UiProtectionBookingsTableComponent extends Component<UiProtectionBookingsTableSignature> {
  @service intl!: Services['intl'];

  @cached
  get rows(): IBookingRow[] {
    return this.args.occasions.map((occasion) => this.toRow(occasion));
  }

  @cached
  get count(): number {
    return this.rows.length;
  }

  private toRow(occasion: ModelRegistry['occasion']): IBookingRow {
    const deposit = occasion.securityDepositStatus;
    const safeguest = occasion.safeguestStatus;
    const claimed = occasion.securityDepositClaimedAmount ?? 0;
    const total = occasion.securityDepositAmount;
    const currency = occasion.bookingCurrency;
    const reading = depositReading(occasion);

    const status = occasionState(occasion);

    return {
      id: occasion.id,
      bookingId: occasion.occasionId,
      guestName: occasion.guestName,
      property: this.propertyLabel(occasion),
      statusKey: `protection_index.status.${status}`,
      statusClass: chipClass(status),
      needsYou: status === ProtectionState.needs,
      depositKey: this.depositKey(reading, safeguest),
      depositAmount: claimed
        ? undefined
        : this.depositAmount(deposit, occasion),
      claimedAmount: claimed ? this.formatAmount(claimed, currency) : undefined,
      depositTotal: claimed ? this.formatAmount(total, currency) : undefined,
      currency,
      identityKey: this.axisKey(
        IDENTITY_KEYS,
        occasion.guestIdentityVerificationStatus,
        'not_required',
      ),
      agreementKey: this.axisKey(
        AGREEMENT_KEYS,
        occasion.rentalAgreementStatus,
        'not_required',
      ),
      stay: this.stay(occasion),
    };
  }

  private propertyLabel(occasion: ModelRegistry['occasion']): string {
    const { propertyNickname, multiUnitName } = occasion;

    return multiUnitName
      ? `${propertyNickname} · ${multiUnitName}`
      : propertyNickname;
  }

  private formatAmount(amount: number, currency: string): string {
    return this.intl.formatNumber(amount, { style: 'currency', currency });
  }

  private depositKey(
    reading: ReturnType<typeof depositReading>,
    safeguest: SafeguestVerificationStatus | undefined,
  ): string {
    if (reading) {
      const key =
        OUTCOME_KEYS[reading as DepositOutcome] ??
        DEPOSIT_KEYS[reading as SecurityDepositStatus] ??
        'none_held';

      return `protection_index.deposit.${key}`;
    }

    if (safeguest) {
      return `protection_index.deposit.${SAFEGUEST_KEYS[safeguest]}`;
    }

    return 'protection_index.deposit.none_held';
  }

  private depositAmount(
    deposit: SecurityDepositStatus | undefined,
    occasion: ModelRegistry['occasion'],
  ): number | undefined {
    const amount = deposit
      ? occasion.securityDepositAmount
      : occasion.safeguestAmount;

    return amount || undefined;
  }

  private axisKey<T extends string>(
    keys: Record<T, string>,
    value: T | undefined,
    fallback: string,
  ): string {
    const key = value ? (keys[value] ?? fallback) : fallback;

    return `protection_index.axis.${key}`;
  }

  private stay(occasion: ModelRegistry['occasion']): string {
    const { checkIn, checkOut } = occasion;

    if (!checkIn || !checkOut) {
      return '';
    }

    const from = parseISO(String(checkIn));
    const to = parseISO(String(checkOut));

    return isSameMonth(from, to)
      ? `${formatDate(checkIn, 'd')} to ${formatDate(checkOut, 'd MMM')}`
      : `${formatDate(checkIn, 'd MMM')} to ${formatDate(checkOut, 'd MMM')}`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Protection::BookingsTable': typeof UiProtectionBookingsTableComponent;
  }
}
